import React, { useEffect, useRef } from "react";
import {
  type DocumentElement as DocumentElementType,
  CONTAINER_TYPE_IDS,
  isHoistedBreakHidden,
} from "@eaco/forms-core";

import {
  useElementAnnotation,
  useElementValue,
  useIsEditor,
  useIsReadOnly,
  useResolvedElementState,
  useResolvedStateMap,
} from "./DocumentContext";
import { useValidation } from "./ValidationContext";
import { useElementRegistry } from "./ElementRegistry";
import { cn } from "../lib/utils";
import { prefersReducedMotion } from "../lib/voiceStyles";

interface Props {
  element: DocumentElementType;
}

/**
 * Engine-side dispatcher for a single `DocumentElement`.
 *
 * Responsibilities (extracted from the old apps/web copy so they live
 * with the engine, not next to the renderers):
 *
 *   - Resolve the renderer for `element.typeId` from the
 *     `ElementRegistry` provided by the host app.
 *   - Apply the `set_value` action once when a logic rule first fires
 *     and the field is currently empty.
 *   - Hide / reveal based on the combined logic-resolved state and the
 *     static `data.hidden` flag. Editor canvas always shows everything.
 *   - Merge logic-driven `required` / `disabled` into the rendered
 *     element's `data` so leaf renderers can stay logic-unaware.
 *   - Wrap the rendered output in `ValidationWrapper` so a failing
 *     required field shows the red bar and scrolls into view on submit.
 */
const DocumentElement: React.FC<Props> = ({ element }) => {
  const registry = useElementRegistry();
  const Component = registry[element.typeId];

  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const renderElementAnnotation = useElementAnnotation();
  const resolvedState = useResolvedElementState(element.id);
  const resolvedStateMap = useResolvedStateMap();
  const [currentValue, writeValue] = useElementValue<unknown>(element.id);

  // Apply set_value action: write the value once when the rule first fires
  // and the field is currently empty. Skipped in editor mode.
  useEffect(() => {
    if (isEditor) return;
    if (!resolvedState || resolvedState.setValue === undefined) return;
    if (
      currentValue === undefined ||
      currentValue === "" ||
      currentValue === null
    ) {
      writeValue(resolvedState.setValue);
    }
    // Intentionally only depends on whether setValue exists for this element,
    // not on currentValue identity, to avoid re-applying on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditor, element.id, resolvedState?.setValue]);

  if (!Component) {
    return (
      <div className="rounded border border-destructive bg-destructive/10 p-2 text-sm text-destructive">
        Unknown element type: {element.typeId}
      </div>
    );
  }

  // Archived elements never render outside the editor: the visibility walk
  // (forms-core logic/visibility.ts) and the native renderer already drop
  // them, so neither the filler nor a response viewer should see them.
  // The editor canvas keeps them with an "archived" banner instead.
  if (!isEditor && element.archived === true) {
    return null;
  }

  // Hide at fill time. Precedence:
  //   1. If a logic rule fired and set hidden (true OR false) — that wins,
  //      so a `show` action can reveal a field whose static `data.hidden` is true.
  //   2. Otherwise fall back to the static `data.hidden` flag from the element config.
  // Editor canvas always shows everything (data.hidden surfaces a banner instead).
  // A page break the binder moved out of a Columns block (`data.hoistedFrom`)
  // stands for every break that block held: it shows while any of them, with
  // the containers it sat under, would have shown in place.
  const dataHidden =
    (element.data as { hidden?: boolean } | undefined)?.hidden === true;
  const hoistedHidden = isHoistedBreakHidden(
    element.data as Record<string, unknown> | undefined,
    resolvedStateMap,
  );
  const isHidden =
    hoistedHidden !== undefined
      ? hoistedHidden
      : resolvedState?.hidden !== undefined
        ? resolvedState.hidden
        : dataHidden;
  if (!isEditor && isHidden) {
    return null;
  }

  // Merge required/disabled from logic into the element's data so leaf field
  // components (which read these from data) pick them up without each having
  // to know about the logic system. Logic value wins when defined.
  const effectiveElement =
    !isEditor &&
    resolvedState &&
    (resolvedState.required !== undefined ||
      resolvedState.disabled !== undefined)
      ? {
          ...element,
          data: {
            ...(element.data || {}),
            ...(resolvedState.required !== undefined
              ? { required: resolvedState.required }
              : {}),
            ...(resolvedState.disabled !== undefined
              ? { disabled: resolvedState.disabled }
              : {}),
          },
        }
      : element;

  // Inline per-element annotation (e.g. sign-off reviewer comments).
  // Only for leaf input elements at fill/view time — structural
  // containers recurse into their own children, which get annotated
  // individually.
  const annotation =
    !isEditor &&
    renderElementAnnotation &&
    !CONTAINER_TYPE_IDS.has(element.typeId)
      ? renderElementAnnotation(element.id)
      : null;

  // A pages stepper owns a full-height sticky-footer layout; let its wrapper
  // grow inside a flex column host (DocumentView single-paged mode) so the
  // footer pins to the bottom of the scroll frame even on short/empty pages.
  const grow = element.typeId === "pages";

  // At fill time every leaf renders inside a fieldset so a fired `disable`
  // action can lock it without remounting it (a remount would rerun mount
  // effects such as the default-value hydration and drop unsaved signature
  // ink). While locked the fieldset is disabled, which turns off every
  // native control in it, pointer events are blocked for the custom
  // (div-based) controls and the field is dimmed; its label and answer stay
  // readable to assistive tech. Containers render bare (the engine resolves
  // state per leaf), and so do the editor canvas and read-only / print
  // renders, which never lock and whose joined-table CSS expects the bare
  // DOM.
  const wrap = !isEditor && !isReadOnly && !CONTAINER_TYPE_IDS.has(element.typeId);
  const isDisabled =
    wrap &&
    (effectiveElement.data as { disabled?: unknown } | undefined)?.disabled === true;

  return (
    <ValidationWrapper
      elementId={element.id}
      typeId={element.typeId}
      grow={grow}
      tableRow={isLinkedTableRow(element)}
    >
      {wrap ? (
        <fieldset
          disabled={isDisabled}
          aria-disabled={isDisabled ? "true" : undefined}
          data-element-disabled={isDisabled ? "" : undefined}
          className={cn(
            "m-0 min-w-0 border-0 p-0",
            isDisabled && "opacity-60 pointer-events-none select-none",
          )}
        >
          <Component element={effectiveElement} />
        </fieldset>
      ) : (
        <Component element={effectiveElement} />
      )}
      {annotation}
    </ValidationWrapper>
  );
};

/**
 * A "Linked Entity Data" field set to the table style. Consecutive ones
 * render as a single table, so the wrapper has to drop the block gap
 * between them and mark itself for the renderer's join rules.
 */
/**
 * Whether the element draws itself as one row of a details table, and so
 * needs the wrapper marking for the CSS that joins a run of them up. The
 * Linked Entity Data family and the form field both offer the style.
 */
function isLinkedTableRow(element: { typeId: string; data?: Record<string, unknown> }): boolean {
  if (!element.typeId.startsWith("linked-") && element.typeId !== "form-field")
    return false;
  const raw = element.data?.style;
  if (raw === "table") return true;
  return (
    Array.isArray(raw) &&
    !!raw[0] &&
    typeof raw[0] === "object" &&
    (raw[0] as { value?: unknown }).value === "table"
  );
}

// Field-status left rail colours — from the forms design tokens (see
// @eaco/forms-core), injected as CSS vars by DocumentView:
//   amber = required & still to complete (a "to-do", NOT an error)
//   red   = validation failure after a submit attempt
const RAIL_AMBER = "var(--forms-rail-amber)";
const RAIL_RED = "var(--forms-rail-red)";

/**
 * Draws the field-status left rail and scrolls the first failing element
 * into view on submit. The rail encodes state, never punishing the filler
 * for scanning ahead:
 *   - amber while a required field is still empty (live "to-do" signal)
 *   - red once a submit attempt marks it invalid
 *   - none when complete or optional
 * Mirrors the public apps/forms surface so the preview behaves the same.
 */
const ValidationWrapper: React.FC<{
  elementId: string;
  children: React.ReactNode;
  grow?: boolean;
  /** The element type (data-pbt): list rhythm rules key on it. */
  typeId: string;
  /** A linked field in the table style — joins to its neighbours. */
  tableRow?: boolean;
}> = ({ elementId, children, grow,
  typeId, tableRow,
}) => {
  const { invalidIds, incompleteElementIds, focusElementId, focusToken } =
    useValidation();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Red only while the field was flagged on a submit attempt AND is still
  // failing. `incompleteElementIds` is recomputed live on every value
  // change, so the moment the filler satisfies the field it drops out of
  // that set and the red clears — no second submit needed.
  const isInvalid =
    invalidIds.has(elementId) && incompleteElementIds.has(elementId);
  // Amber "to-do" only while not already flagged red.
  const isIncomplete = !isInvalid && incompleteElementIds.has(elementId);
  const railColor = isInvalid ? RAIL_RED : isIncomplete ? RAIL_AMBER : null;
  const shouldFocus = focusElementId === elementId;

  // Re-runs each time the user clicks Submit and the same element is
  // still the first failing one (focusToken bumps even when the id
  // matches the previous attempt).
  useEffect(() => {
    if (!shouldFocus) return;
    const t = window.setTimeout(() => {
      wrapperRef.current?.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "center",
      });
    }, 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFocus, focusToken]);

  return (
    <div
      ref={wrapperRef}
      data-element-id={elementId}
      data-pbt={typeId}
      data-linked-table={tableRow ? "" : undefined}
      className={cn(
        grow ? "relative flex min-h-0 flex-1 flex-col" : "relative",
        // List rhythm, matching the editor canvas: consecutive items of the
        // same list type sit close together instead of a full block gap
        // apart. The container's space-y sets the gap as margin-top, so the
        // override has to win with !important.
        // Both edges, for the same two-majors reason as the table rows
        // below: on v4 the gap is the PRECEDING item's margin-bottom, so a
        // margin-top override alone left a run of bullets a full block gap
        // apart in the web app.
        typeId === "page-bullet-list" &&
          "[[data-pbt=page-bullet-list]+&]:!mt-[5px] [&:has(+[data-pbt=page-bullet-list])]:!mb-0",
        typeId === "page-numbered-list" &&
          "[[data-pbt=page-numbered-list]+&]:!mt-[5px] [&:has(+[data-pbt=page-numbered-list])]:!mb-0",
        typeId === "page-todo-list" &&
          "[[data-pbt=page-todo-list]+&]:!mt-[5px] [&:has(+[data-pbt=page-todo-list])]:!mb-0",
        // Stacked table-style linked fields are one table, so the block gap
        // between them goes entirely. Both edges are killed because the two
        // Tailwind majors put `space-y` on opposite sides — v3 sets
        // margin-top on the following sibling, v4 margin-bottom on the
        // preceding one — and this package is compiled by both (the web app
        // on v4, the PDF service on v3). Same !important reason as above.
        tableRow && "[[data-linked-table]+&]:!mt-0",
        tableRow && "[&:has(+[data-linked-table])]:!mb-0",
      )}
    >
      {railColor && (
        <span
          aria-hidden
          className="pointer-events-none absolute -left-3 top-0 h-full w-1 rounded-full"
          style={{ backgroundColor: railColor }}
        />
      )}
      {children}
    </div>
  );
};

export default DocumentElement;
